import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    MeetingChip,
    MeetingChipList,
    MeetingChipRemove,
    MeetingCompactActionButton,
    MeetingFieldActionRow,
    MeetingFieldActions,
    MeetingFieldGrid,
    MeetingFieldIconButton,
    MeetingFieldInputWrap,
    MeetingInlineActionRow,
    MeetingInlineActionText,
    MeetingPanel,
    MeetingPanelHeader,
    MeetingPanelText,
    MeetingPanelTitle,
    MeetingStatusCard,
} from "src/components";
import type { CalendarItemForm, CompanyUser, ThemeMode } from "../types";
import { SHARED_AUDIENCE, TASK_REPEAT_LABEL, TASK_REPEAT_OPTIONS } from "../calendarData";
import { CalendarSelectChevron, CalendarSelectWrap } from "./common";
import { Label, TaskFieldInput, TaskFieldSelect } from "./modal";
import { FilterItem, FilterLabel, Toggle } from "./shell";
import { CalendarParticipantSelectionModal } from "./CalendarParticipantSelectionModal";

type CalendarMeetingSectionProps = {
    companyUsers: CompanyUser[];
    companyUsersLoading: boolean;
    currentUserUuid: string;
    form: CalendarItemForm;
    hasStoredPassword: boolean;
    setForm: React.Dispatch<React.SetStateAction<CalendarItemForm>>;
    themeMode: ThemeMode;
};

const parseGuestEmails = (value: string): string[] => Array.from(new Set(value
    .split(/[\s,;]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean)));

const generatePassword = (): string => {
    const values = new Uint32Array(4);
    window.crypto.getRandomValues(values);
    return Array.from(values).map(value => value.toString(36)).join("-").slice(0, 20);
};

export function CalendarMeetingSection({
    companyUsers,
    companyUsersLoading,
    currentUserUuid,
    form,
    hasStoredPassword,
    setForm,
    themeMode,
}: CalendarMeetingSectionProps) {
    const [showPassword, setShowPassword] = useState(false);
    const [passwordCopied, setPasswordCopied] = useState(false);
    const [isReplacingStoredPassword, setIsReplacingStoredPassword] = useState(false);
    const [participantModalIsOpen, setParticipantModalIsOpen] = useState(false);
    const [guestEmailDraft, setGuestEmailDraft] = useState("");
    const [guestEmailError, setGuestEmailError] = useState("");
    const copyFeedbackTimeoutRef = useRef<number | null>(null);
    const selectedInternalUsers = useMemo(() => new Set(form.meeting.internalParticipantUuids), [form.meeting.internalParticipantUuids]);
    const passwordInputIsVisible = !hasStoredPassword
        || isReplacingStoredPassword
        || form.meeting.password.length > 0;

    useEffect(() => () => {
        if (copyFeedbackTimeoutRef.current !== null) {
            window.clearTimeout(copyFeedbackTimeoutRef.current);
        }
    }, []);

    const updateMeeting = (updates: Partial<CalendarItemForm["meeting"]>) => {
        setForm(previous => ({
            ...previous,
            meeting: { ...previous.meeting, ...updates },
        }));
    };

    const updateInternalParticipants = (participantUuids: string[]) => {
        const selectedParticipants = new Set(participantUuids);
        const employeeUuids = companyUsers
            .filter(companyUser => !companyUser.isClient && selectedParticipants.has(companyUser.uuid))
            .map(companyUser => companyUser.uuid);
        const clientUuids = companyUsers
            .filter(companyUser => companyUser.isClient && selectedParticipants.has(companyUser.uuid))
            .map(companyUser => companyUser.uuid);
        const audience = employeeUuids.length > 0 && clientUuids.length > 0
            ? SHARED_AUDIENCE.BOTH
            : employeeUuids.length > 0
                ? SHARED_AUDIENCE.EMPLOYEES
                : clientUuids.length > 0
                    ? SHARED_AUDIENCE.CLIENTS
                    : SHARED_AUDIENCE.PERSONAL;

        setForm(previous => ({
            ...previous,
            audience,
            visibilityRecipients: {
                employees: { all: false, selectedEmployeeUuids: employeeUuids },
                clients: { all: false, selectedEmployeeUuids: clientUuids },
            },
            meeting: { ...previous.meeting, internalParticipantUuids: participantUuids },
        }));
    };

    const selectedInternalParticipants = useMemo(
        () => companyUsers.filter(companyUser => (
            companyUser.uuid !== currentUserUuid && selectedInternalUsers.has(companyUser.uuid)
        )),
        [companyUsers, currentUserUuid, selectedInternalUsers]
    );

    const addGuestEmails = (value: string) => {
        const emails = parseGuestEmails(value);
        const invalidEmail = emails.find(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        if (invalidEmail) {
            setGuestEmailError(`Enter a valid email address: ${invalidEmail}`);
            return false;
        }
        if (emails.length === 0) {
            return false;
        }
        updateMeeting({ guestEmails: Array.from(new Set([...form.meeting.guestEmails, ...emails])) });
        setGuestEmailDraft("");
        setGuestEmailError("");
        return true;
    };

    const handleGuestEmailChange = (value: string) => {
        if (!/[;,]/.test(value)) {
            setGuestEmailDraft(value);
            setGuestEmailError("");
            return;
        }
        const segments = value.split(/[;,]/);
        const completedValue = segments.slice(0, -1).join(",");
        const remainder = segments[segments.length - 1]?.trim() || "";
        if (addGuestEmails(completedValue)) {
            setGuestEmailDraft(remainder);
        }
    };

    const copyMeetingPassword = async (): Promise<void> => {
        if (!form.meeting.password || !navigator.clipboard) {
            return;
        }
        await navigator.clipboard.writeText(form.meeting.password);
        setPasswordCopied(true);
        if (copyFeedbackTimeoutRef.current !== null) {
            window.clearTimeout(copyFeedbackTimeoutRef.current);
        }
        copyFeedbackTimeoutRef.current = window.setTimeout(() => {
            setPasswordCopied(false);
            copyFeedbackTimeoutRef.current = null;
        }, 1800);
    };

    return (
        <MeetingPanel>
            <MeetingPanelHeader>
                <div>
                    <MeetingPanelTitle>Me Meets</MeetingPanelTitle>
                    <MeetingPanelText>Meet online with Me Business users and external browser guests.</MeetingPanelText>
                </div>
                <Toggle
                    active={form.meeting.enabled}
                    type="button"
                    aria-label="Create a Me Meets meeting"
                    onClick={() => updateMeeting({ enabled: !form.meeting.enabled })}
                />
            </MeetingPanelHeader>
            {!form.meeting.enabled && form.meetingUrl && (
                <div>
                    <Label>External Meeting Link</Label>
                    <TaskFieldInput
                        themeMode={themeMode}
                        value={form.meetingUrl}
                        onChange={({ target: { value } }) => setForm(previous => ({ ...previous, meetingUrl: value }))}
                        placeholder="https://…"
                    />
                </div>
            )}
            {form.meeting.enabled && (
                <>
                    <MeetingFieldGrid>
                        <div>
                            <Label>Meeting Link</Label>
                            <TaskFieldInput
                                themeMode={themeMode}
                                value={form.meetingUrl}
                                placeholder="Generated securely when you save"
                                readOnly
                            />
                        </div>
                        <div>
                            <Label>Timezone</Label>
                            <TaskFieldInput
                                themeMode={themeMode}
                                value={form.meeting.timezone}
                                onChange={({ target: { value } }) => updateMeeting({ timezone: value })}
                            />
                        </div>
                        {form.repeat !== TASK_REPEAT_OPTIONS.NONE && (
                            <div>
                                <Label>Repeat Until</Label>
                                <TaskFieldInput
                                    themeMode={themeMode}
                                    type="date"
                                    min={form.startDate}
                                    value={form.meeting.recurrenceEndsAt || ""}
                                    onChange={({ target: { value } }) => updateMeeting({
                                        recurrenceEndsAt: value || null,
                                    })}
                                />
                            </div>
                        )}
                    </MeetingFieldGrid>
                    <div>
                        <Label>Internal Participants</Label>
                        <MeetingInlineActionRow>
                            <MeetingInlineActionText>
                                {companyUsersLoading
                                    ? "Loading your company directory…"
                                    : `${selectedInternalParticipants.length} selected`}
                            </MeetingInlineActionText>
                            <MeetingCompactActionButton
                                $small
                                type="button"
                                disabled={companyUsersLoading}
                                onClick={() => setParticipantModalIsOpen(true)}
                            >
                                {selectedInternalParticipants.length > 0 ? "Edit Participants" : "Select Participants"}
                            </MeetingCompactActionButton>
                        </MeetingInlineActionRow>
                        <CalendarParticipantSelectionModal
                            companyUsers={companyUsers}
                            companyUsersLoading={companyUsersLoading}
                            currentUserUuid={currentUserUuid}
                            open={participantModalIsOpen}
                            selectedParticipantUuids={form.meeting.internalParticipantUuids}
                            onClose={() => setParticipantModalIsOpen(false)}
                            onSave={updateInternalParticipants}
                        />
                    </div>
                    <div>
                        <Label>Invite External Guests</Label>
                        <TaskFieldInput
                            themeMode={themeMode}
                            type="email"
                            value={guestEmailDraft}
                            onChange={({ target: { value } }) => handleGuestEmailChange(value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === "," || event.key === ";") {
                                    event.preventDefault();
                                    addGuestEmails(guestEmailDraft);
                                }
                            }}
                            onBlur={() => {
                                if (guestEmailDraft.trim()) {
                                    addGuestEmails(guestEmailDraft);
                                }
                            }}
                            placeholder="Type an email, then press Enter"
                        />
                        {guestEmailError && <MeetingPanelText>{guestEmailError}</MeetingPanelText>}
                        <MeetingChipList>
                            {form.meeting.guestEmails.map(email => (
                                <MeetingChip key={email}>
                                    {email}
                                    <MeetingChipRemove
                                        type="button"
                                        aria-label={`Remove ${email}`}
                                        onClick={() => updateMeeting({
                                            guestEmails: form.meeting.guestEmails.filter(item => item !== email),
                                        })}
                                    >
                                        ×
                                    </MeetingChipRemove>
                                </MeetingChip>
                            ))}
                        </MeetingChipList>
                        <FilterItem as="div" style={{ minHeight: 42 }}>
                            <FilterLabel style={{ minWidth: 0, flex: 1, textAlign: "left", marginRight: ".9rem" }}>
                                Require Meeting Password
                            </FilterLabel>
                            <Toggle
                                active={form.meeting.passwordEnabled}
                                type="button"
                                onClick={() => updateMeeting({ passwordEnabled: !form.meeting.passwordEnabled, password: "" })}
                            />
                        </FilterItem>
                        {form.meeting.passwordEnabled && (
                            <MeetingFieldGrid>
                                <div>
                                    <Label>{hasStoredPassword && passwordInputIsVisible
                                        ? "Replacement Meeting Password"
                                        : "Meeting Password"}</Label>
                                    <MeetingFieldInputWrap>
                                        <TaskFieldInput
                                            autoComplete="new-password"
                                            disabled={!passwordInputIsVisible}
                                            name="meeting-password-replacement"
                                            themeMode={themeMode}
                                            type={showPassword ? "text" : "password"}
                                            value={form.meeting.password}
                                            onChange={({ target: { value } }) => updateMeeting({ password: value })}
                                            placeholder={passwordInputIsVisible ? "At least 8 characters" : "Saved password is hidden"}
                                            $trailingActionCount={passwordCopied ? 3 : 2}
                                        />
                                        <MeetingFieldActions>
                                            <MeetingFieldIconButton
                                                type="button"
                                                aria-label={showPassword ? "Hide meeting password" : "Show meeting password"}
                                                title={form.meeting.password
                                                    ? (showPassword ? "Hide password" : "Show password")
                                                    : "Saved passwords cannot be viewed"}
                                                disabled={!form.meeting.password}
                                                onClick={() => setShowPassword(value => !value)}
                                            >
                                                {showPassword ? (
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                        <path d="M3 3l18 18" />
                                                        <path d="M10.6 10.6a2 2 0 002.8 2.8" />
                                                        <path d="M9.9 4.2A10.8 10.8 0 0112 4c5.5 0 9 5.5 9 8a8.8 8.8 0 01-2 3.4M6.6 6.6C4.3 8.1 3 10.5 3 12c0 2.5 3.5 8 9 8 1.3 0 2.5-.3 3.5-.8" />
                                                    </svg>
                                                ) : (
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                        <path d="M3 12s3.5-8 9-8 9 8 9 8-3.5 8-9 8-9-8-9-8z" />
                                                        <circle cx="12" cy="12" r="2.5" />
                                                    </svg>
                                                )}
                                            </MeetingFieldIconButton>
                                            <MeetingFieldIconButton
                                                type="button"
                                                aria-label="Copy meeting password"
                                                title={form.meeting.password ? "Copy password" : "Saved passwords cannot be copied"}
                                                disabled={!form.meeting.password}
                                                $feedback={passwordCopied}
                                                onClick={copyMeetingPassword}
                                            >
                                                {passwordCopied ? "Copied" : (
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                        <rect x="8" y="8" width="12" height="12" rx="2" />
                                                        <path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" />
                                                    </svg>
                                                )}
                                            </MeetingFieldIconButton>
                                        </MeetingFieldActions>
                                    </MeetingFieldInputWrap>
                                </div>
                                <MeetingFieldActionRow style={{ gap: ".5rem" }}>
                                    {passwordInputIsVisible ? (
                                        <>
                                            {hasStoredPassword && (
                                                <MeetingCompactActionButton
                                                    type="button"
                                                    onClick={() => {
                                                        updateMeeting({ password: "" });
                                                        setIsReplacingStoredPassword(false);
                                                        setShowPassword(false);
                                                    }}
                                                >
                                                    Keep Current
                                                </MeetingCompactActionButton>
                                            )}
                                            <MeetingCompactActionButton
                                                type="button"
                                                onClick={() => {
                                                    setIsReplacingStoredPassword(true);
                                                    updateMeeting({ password: generatePassword() });
                                                }}
                                            >
                                                Generate
                                            </MeetingCompactActionButton>
                                        </>
                                    ) : (
                                        <MeetingCompactActionButton
                                            type="button"
                                            onClick={() => setIsReplacingStoredPassword(true)}
                                        >
                                            Replace Password
                                        </MeetingCompactActionButton>
                                    )}
                                </MeetingFieldActionRow>
                                {!passwordInputIsVisible && (
                                    <MeetingStatusCard $fullWidth>
                                        Stored securely. It will remain unchanged unless you replace it.
                                    </MeetingStatusCard>
                                )}
                                {form.meeting.password && (
                                    <MeetingStatusCard $fullWidth>
                                        Copy before saving. It won’t be shown again. Invitees will receive it by email.
                                    </MeetingStatusCard>
                                )}
                            </MeetingFieldGrid>
                        )}
                        <MeetingPanelText>
                            {form.meeting.passwordEnabled
                                ? "External guests need the meeting password and host approval. Same-company users bypass both."
                                : "External guests require host approval. Same-company users are admitted automatically."}
                        </MeetingPanelText>
                    </div>
                    <MeetingFieldGrid>
                        <div>
                            <Label>Repeat</Label>
                            <CalendarSelectWrap $fullWidth>
                                <TaskFieldSelect
                                    themeMode={themeMode}
                                    value={form.repeat}
                                    onChange={({ target: { value } }) => setForm(previous => ({
                                        ...previous,
                                        repeat: value as CalendarItemForm["repeat"],
                                    }))}
                                >
                                    {Object.values(TASK_REPEAT_OPTIONS).map(option => (
                                        <option key={option} value={option}>{TASK_REPEAT_LABEL[option]}</option>
                                    ))}
                                </TaskFieldSelect>
                                <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                            </CalendarSelectWrap>
                        </div>
                    </MeetingFieldGrid>
                </>
            )}
        </MeetingPanel>
    );
}
